import * as R from 'ramda'
import { type NamedEntity, type Nil, Utils } from '@pbt/pbt-ui-components'

import type { InvoiceLineItem, InvoiceLineItemGroup } from '~/types'
import { arrayToMap } from '~/utils'
import {
  addRange,
  getLowValue,
  isEveryItemOfSingleQuantity,
} from '~/utils/invoiceUtils'

export function isEveryGroupOfSingleQuantity(groups: InvoiceLineItemGroup[]) {
  return groups.every((group) =>
    isEveryItemOfSingleQuantity(group.groupedItems),
  )
}

/**
 * Fixed bundle price may live on the parent line item and/or on children (e.g. after
 * convertToLineItems enrichment when the parent row has no `group` in the API payload).
 */
export function getBundleFixedPriceFromLineItem(
  parent: InvoiceLineItem | null | undefined,
): number | null {
  if (!parent) {
    return null
  }
  if (parent.fixedPrice != null) {
    return parent.fixedPrice
  }
  const fromChild = parent.items?.find((c) => c.fixedPrice != null)?.fixedPrice
  return fromChild ?? null
}

const getPriceId = (item: InvoiceLineItem) =>
  R.path(['price', 'id'], item) || R.prop('priceId', item)
export const getLineItemUniqId = (item: InvoiceLineItem) =>
  `${R.prop('logType', item)}-${getPriceId(item)}-${R.prop('group', item)}`

export const markManualDiscountSource = (
  DiscountSource: NamedEntity[],
  lineItem: InvoiceLineItem,
) => {
  lineItem.discountSourceId = Utils.findByName('Manual', DiscountSource)?.id
}

/**
 * True when the user edited discount in the invoice UI (Manual source).
 * Used when saving estimates so fixed-price bundle children do not send
 * computed discountAmount/discountPerc unless the line was manually discounted.
 */
export const isManualDiscountInvoiceLineItem = (
  item: InvoiceLineItem,
  discountSourceConstants: NamedEntity[],
): boolean => {
  const manualId = Utils.findByName('Manual', discountSourceConstants)?.id
  const nameIsManual =
    typeof item.discountSource?.name === 'string' &&
    item.discountSource.name.trim().toLowerCase() === 'manual'
  return nameIsManual || Boolean(manualId && item.discountSourceId === manualId)
}

export const producerFieldChanged = (
  item: InvoiceLineItem,
  newItem: InvoiceLineItem,
) => {
  newItem.producerIdChanged = true
}

export function mergeItemsWithQuantitySumLegacy(
  array1: any[],
  array2: any[],
  idFieldOrGetter: string | Function = 'priceId',
): any[] {
  const getId =
    typeof idFieldOrGetter === 'function'
      ? idFieldOrGetter
      : R.prop(idFieldOrGetter)

  return Object.values(
    array1.concat(array2).reduce((acc, item) => {
      const id = getId(item)
      const oldItem = acc[id]
      const oldQuantity = oldItem ? oldItem.quantity : 0
      const oldUsedQuantity = oldItem?.usedQuantity
      const quantity = addRange(oldQuantity, item.quantity)
      const prepaid = oldItem?.prepaid || item.prepaid
      const usedQuantity = R.isNil(oldUsedQuantity)
        ? R.isNil(item.usedQuantity)
          ? null
          : item.usedQuantity
        : R.isNil(item.usedQuantity)
          ? oldUsedQuantity
          : oldUsedQuantity + item.usedQuantity

      return {
        ...acc,
        [id]: {
          ...(oldItem || item),
          quantity,
          ...(R.isNil(usedQuantity) ? {} : { usedQuantity }),
          ...(prepaid ? { prepaid } : {}),
        },
      }
    }, {}),
  )
}

export const insertCandidatesIntoGroup = (
  candidates: InvoiceLineItem[],
  group: InvoiceLineItemGroup,
  isSetBundlePriceEnabled = false,
) => {
  const newGroup = R.clone(group)

  candidates.forEach((item) => {
    const isGrouped = Boolean(item.group)

    if (isGrouped) {
      const groupIndex = newGroup.groupedItems.findIndex(
        (groupedItem: InvoiceLineItem) => groupedItem.group === item.group,
      )

      if (groupIndex > -1) {
        const existing = newGroup.groupedItems[groupIndex]
        existing.items = mergeItemsWithQuantitySumLegacy(
          existing.items || [],
          [item],
          getLineItemUniqId,
        )
        if (isSetBundlePriceEnabled && item.fixedPrice != null) {
          existing.fixedPrice = item.fixedPrice
        }
      } else {
        newGroup.groupedItems.push({
          group: item.group,
          groupName: item.groupName,
          items: item.items || [item],
          ...(isSetBundlePriceEnabled && {
            fixedPrice: item.fixedPrice ?? null,
          }),
        } as InvoiceLineItem)
      }
    } else {
      newGroup.groupedItems = mergeItemsWithQuantitySumLegacy(
        newGroup.groupedItems,
        [item],
        getLineItemUniqId,
      )
    }
  })

  return newGroup
}

const isDefinitelyZero = (number: number | Nil) => Number(number) === 0

export const getItemHasZeroQuantity = (item: InvoiceLineItem) =>
  item.items
    ? item.items.some(getItemHasZeroQuantity)
    : item.prepaid
      ? isDefinitelyZero(item.quantity) && isDefinitelyZero(item.usedQuantity)
      : isDefinitelyZero(item.quantity)

export const getItemsHaveZeroQuantity = (groups: InvoiceLineItemGroup[]) =>
  groups.some((group) => group.groupedItems.some(getItemHasZeroQuantity))

export const updateItemsToUseSingleQuantity = (
  items: InvoiceLineItem[],
): InvoiceLineItem[] =>
  items.map((item) =>
    item.items
      ? { ...item, items: updateItemsToUseSingleQuantity(item.items) }
      : { ...item, quantity: Math.max(getLowValue(item.quantity), 1) },
  )

export const mergeOrderedItemsLegacy = R.curry(
  (
    getUniqId: (item: any) => string,
    newOrderedItems: any[],
    prevOrderedItems: any[],
  ) => {
    const orderedItemsMap = arrayToMap(prevOrderedItems, getUniqId)
    newOrderedItems.forEach((newItem) => {
      const itemId = getUniqId(newItem)
      const oldItem = orderedItemsMap[itemId]
      orderedItemsMap[itemId] = R.mergeRight(oldItem, newItem)
    })

    return R.values(orderedItemsMap)
  },
)

export const getGroupId = (group: InvoiceLineItemGroup) =>
  group.soap?.id || 'otc'

export const isAnyItemPrepaid = (
  invoiceLineItems: InvoiceLineItem[],
): boolean =>
  invoiceLineItems.some(
    (ili) =>
      (ili.prepaid && !ili.declined) ||
      (!R.isNil(ili.items) &&
        ili.items.some((subIli) => subIli.prepaid && !subIli.declined)),
  )
